
import React, { useState, useEffect } from 'react';
import type { User } from '../App';
import { useStoreBootstrap } from '../contexts/StoreBootstrap';
import LanguageSwitcher from './LanguageSwitcher';
import { wishlistStore } from '../utils/wishlistStore';

const UserIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);


const GarageGlyph: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 16l1.5-5.5A2 2 0 018.4 9h7.2a2 2 0 011.9 1.5L19 16M4 16h16v3a1 1 0 01-1 1h-1a1 1 0 01-1-1v-1H7v1a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zm3.5-3h.01M16.5 13h.01" />
    </svg>
);

const GarageIcon: React.FC = () => (
    <a href="/account?section=garage" aria-label="Mój garaż" title="Mój garaż" className="relative text-cyan-200/80 hover:text-cyan-100 transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 16l1.5-5.5A2 2 0 018.4 9h7.2a2 2 0 011.9 1.5L19 16M4 16h16v3a1 1 0 01-1 1h-1a1 1 0 01-1-1v-1H7v1a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zm3.5-3h.01M16.5 13h.01" />
        </svg>
    </a>
);

const CartIcon: React.FC<{ count: number }> = ({ count }) => (
    <a href="/cart" aria-label={`Koszyk, ${count} ${count === 1 ? 'produkt' : 'produktów'}`} className="relative text-cyan-200/80 hover:text-cyan-100 transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        <span className="fc-cart-count absolute -top-2 -right-2 bg-gradient-to-r from-cyan-500 to-emerald-400 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold shadow-md" aria-label={`${count} produktów`}>
            {count}
        </span>
    </a>
);

const WishlistIcon: React.FC<{ count: number }> = ({ count }) => (
    <a href="/wishlist" aria-label={`Ulubione, ${count} produktów`} className="relative text-cyan-200/80 hover:text-red-400 transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill={count > 0 ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true" style={count > 0 ? {color: '#f87171'} : {}}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
        {count > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold shadow-md">
                {count}
            </span>
        )}
    </a>
);


const HamburgerIcon: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <button onClick={onClick} className="p-1 text-cyan-200/70 hover:text-cyan-200 transition-colors" aria-label="Open menu">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
    </button>
);

const CloseIcon: React.FC<{ onClick: () => void }> = ({ onClick }) => (
     <button onClick={onClick} className="p-1 text-cyan-200/70 hover:text-cyan-200 transition-colors" aria-label="Close menu">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
    </button>
);

const HomeIcon: React.FC = () => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        className="h-5 w-5" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
    >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
);

const AlloyIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="12" r="9" className="stroke-current" />
        <path d="M12 3v18M21 12H3M18.36 5.64l-12.72 12.72M5.64 5.64l12.72 12.72" className="stroke-current" strokeLinecap="round" />
    </svg>
);

const SparkIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 2l1.9 5.8h6.1l-4.95 3.6 1.9 5.8L12 13.6 7 17.2l1.9-5.8L4 7.8h6.1L12 2z" />
    </svg>
);

const FlameIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3s-3 3-3 6a3 3 0 006 0c0-3-3-6-3-6z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21c3.866 0 7-2.462 7-5.5 0-2.086-1.486-3.889-3.593-4.748" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21c-3.866 0-7-2.462-7-5.5 0-2.086 1.486-3.889 3.593-4.748" />
    </svg>
);

const CalcIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <rect x="4" y="2" width="16" height="20" rx="2" />
        <path strokeLinecap="round" d="M8 7h2M12 7h4M8 12h2M12 12h4M8 17h2M12 17h4" />
    </svg>
);

const WheelFitIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <circle cx="11" cy="11" r="7" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
    </svg>
);

const PhoneIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h2.6a1 1 0 01.96.72l1.1 3.85a1 1 0 01-.54 1.17l-1.5.75a12.05 12.05 0 006.02 6.02l.75-1.5a1 1 0 011.17-.54l3.85 1.1a1 1 0 01.72.96V19a2 2 0 01-2 2h-1C8.268 21 3 15.732 3 9V7a2 2 0 012-2z" />
    </svg>
);

const DownloadIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
);

interface NavItem {
    name: string;
    href: string;
    icon: React.ReactNode;
    accent?: string;
}

interface HeaderProps {
    cartCount: number;
    currentUser: User | null;
    onLogout: () => void;
}


const Header: React.FC<HeaderProps> = ({ cartCount, currentUser, onLogout }) => {
    const bootstrap = useStoreBootstrap();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [categories, setCategories] = useState<Array<{ id: number; name: string; slug: string; icon: string | null }>>([]);
    const [showCategoriesMenu, setShowCategoriesMenu] = useState(false);
    const [showToolsMenu, setShowToolsMenu] = useState(false);
    const [menuTimeout, setMenuTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [homepageSettings, setHomepageSettings] = useState<any>({ categories: true });
    const [wishlistCount, setWishlistCount] = useState(bootstrap ? 0 : wishlistStore.getCount());
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [isIOS, setIsIOS] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);

    useEffect(() => {
        // Detekcja iOS
        const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
        setIsIOS(ios);

        // Detekcja czy aplikacja już jest zainstalowana
        const standalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
        setIsStandalone(standalone);

        const handleBeforeInstallPrompt = (e: any) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                setDeferredPrompt(null);
            }
        } else {
            // Instrukcja dla iOS lub innych przeglądarek bez wsparcia dla prompt()
            const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
            if (isIOSDevice) {
                alert("Aby zainstalować aplikację na iOS:\n1. Kliknij przycisk 'Udostępnij' (na dole przeglądarki Safari).\n2. Wybierz opcję 'Do ekranu początkowego'.");
            } else {
                alert("Aby zainstalować aplikację:\nOtwórz menu przeglądarki (trzy kropki) i wybierz 'Zainstaluj aplikację' lub 'Dodaj do ekranu głównego'.");
            }
        }
    };

    const handleMouseEnter = () => {
        if (menuTimeout) {
            clearTimeout(menuTimeout);
            setMenuTimeout(null);
        }
        setShowCategoriesMenu(true);
    };

    useEffect(() => {
        setWishlistCount(wishlistStore.getCount());
        const unsub = wishlistStore.subscribe(() => setWishlistCount(wishlistStore.getCount()));
        return unsub;
    }, []);

    const handleMouseLeave = () => {
        const timeout = setTimeout(() => {
            setShowCategoriesMenu(false);
        }, 300); // 300ms delay before closing
        setMenuTimeout(timeout);
    };
    
    // Load categories from API
    useEffect(() => {
        const loadCategories = async () => {
            try {
                const response = await fetch('/api/categories');
                if (response.ok) {
                    const data = await response.json();
                    setCategories(data);
                }
            } catch (error) {
                console.error('Error loading categories:', error);
            }
        };

        const loadSettings = async () => {
            try {
                const res = await fetch('/api/settings/homepage_sections');
                if (res.ok) {
                    const data = await res.json();
                    setHomepageSettings(data);
                }
            } catch (error) {
                console.error('Error loading settings in header:', error);
            }
        };

        loadCategories();
        loadSettings();
    }, []);
    
    const navItems: NavItem[] = [
        { name: 'Strona główna', href: '/', icon: <HomeIcon />, accent: 'Powrót do startu' },
        { name: 'Dobór felg', href: '/dobor-felg', icon: <WheelFitIcon />, accent: 'Felgi do Twojego auta' },
        { name: 'Felgi aluminiowe', href: '/products/felgi-aluminiowe', icon: <AlloyIcon />, accent: 'Aluminiowe felgi' },
        { name: 'NOWOŚCI', href: '/newproducts', icon: <SparkIcon />, accent: 'Świeże dostawy' },
        { name: 'Promocje', href: '/promotions', icon: <FlameIcon />, accent: 'Okazje tygodnia' },
        { name: 'Kontakt', href: '/contact', icon: <PhoneIcon />, accent: 'Porozmawiajmy' }
    ];

    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isMenuOpen]);

    return (
        <header className="fc-header sticky top-0 z-[100] border-b border-cyan-900/30 bg-[#0b111d] lg:bg-gradient-to-r lg:from-[#0b111d]/98 lg:via-[#121a28]/97 lg:to-[#0b111d]/98 shadow-[0_18px_35px_-25px_rgba(12,255,255,0.25)] lg:backdrop-blur-md">
            <div className="w-full px-3 xs:px-4 sm:px-6 lg:px-6 xl:px-8">
            <div className="flex flex-col gap-0 sm:gap-2 lg:gap-4 pb-0 sm:pb-2 lg:pb-6">
                {/* Desktop Navigation - Hidden on mobile/tablet */}
                <div className="fc-desktop-header hidden lg:flex flex-col gap-4">
                    <div className="fc-header-top flex items-center justify-between mb-0 pt-4 px-2">
                        <a href="/" className="fc-logo-link" aria-label="FelgoCar — strona główna">
                            <picture>
                                <img src="/logo/felgocar-main-20261020.webp" alt="FelgoCar.pl" width="660" height="220" className="fc-site-logo" fetchPriority="high" decoding="async" />
                            </picture>
                        </a>
                    </div>
                    <div className="fc-header-nav w-full rounded-2xl border border-cyan-500/20 bg-cyan-500/5 px-3 xl:px-6 py-4 shadow-inner shadow-cyan-500/10 mt-2">
                        <div className="fc-header-nav-row flex items-center justify-between gap-2 xl:gap-4 min-w-0">
                            <div className="flex items-center gap-1 xl:gap-2 min-w-0 flex-1">
                                {/* Strona Główna */}
                                <a
                                    key={navItems[0].name}
                                    href={navItems[0].href}
                                    className="group flex items-center gap-1.5 xl:gap-2 rounded-xl px-2 xl:px-3 py-2 font-semibold text-gray-200 transition-all hover:bg-white/5 hover:text-white whitespace-nowrap flex-shrink-0"
                                >
                                    <span className="flex h-8 w-8 xl:h-9 xl:w-9 items-center justify-center rounded-lg bg-cyan-500/10 text-cyan-300 group-hover:bg-cyan-500/20 group-hover:text-white transition-all">
                                        {navItems[0].icon}
                                    </span>
                                    <span className="flex flex-col text-left">
                                        <span className="uppercase tracking-[0.08em] text-[11px] xl:text-xs leading-tight font-bold">{navItems[0].name}</span>
                                    </span>
                                </a>

                                {/* Kategorie produktów - Synchronized with homepage toggles */}
                                <div 
                                    className="relative mx-1"
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    <button
                                        onClick={() => setShowCategoriesMenu(!showCategoriesMenu)}
                                        aria-label="Pokaż kategorie produktów"
                                        aria-expanded={showCategoriesMenu}
                                        className={`group flex items-center gap-2 rounded-xl px-2 xl:px-4 py-2 text-xs font-bold transition-all whitespace-nowrap ${showCategoriesMenu ? 'bg-cyan-500/20 text-white ring-1 ring-cyan-500/40 shadow-[0_0_20px_rgba(34,211,238,0.2)]' : 'text-cyan-400 hover:bg-cyan-500/10'}`}
                                    >
                                        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-cyan-400 text-[#0b111d] shadow-[0_0_15px_rgba(34,211,238,0.6)] group-hover:scale-110 transition-transform">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16m-7 6h7" />
                                            </svg>
                                        </span>
                                        <span className="uppercase tracking-[0.15em] text-[10px] xl:text-[11px]">Kategorie</span>
                                        <svg className={`w-3 h-3 ml-0.5 transition-transform duration-300 ${showCategoriesMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                    
                                    {/* Dropdown menu */}
                                    <div className={`absolute top-full left-0 mt-3 w-[560px] bg-[#0d121f] border border-cyan-500/30 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.8),0_0_30px_rgba(34,211,238,0.2)] z-[60] transition-all duration-300 origin-top-left ${showCategoriesMenu ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'}`}>
                                        <div className="p-4 max-h-[80vh] overflow-y-auto custom-scrollbar">
                                            <a
                                                href="/products"
                                                aria-label="Przeglądaj wszystkie produkty w sklepie"
                                                className="flex items-center justify-between px-4 py-3 text-[11px] font-black uppercase tracking-[0.2em] text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 rounded-xl transition-all border border-cyan-500/20 mb-3"
                                            >
                                                Felgi aluminiowe
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                                </svg>
                                            </a>
                                            <div className="h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent mb-3" />
                                            <div className="grid grid-cols-2 gap-2">
                                                {categories.filter(category => {
                                                    if (category.slug === 'felgi-aluminiowe' && homepageSettings.categoryAluminum === false) return false;
                                                    if (category.slug === 'akcesoria' && homepageSettings.categoryAccessories === false) return false;
                                                    if (category.slug === 'dystanse' && homepageSettings.categorySpacers === false) return false;
                                                    if (category.slug === 'nakretki' && homepageSettings.categoryNuts === false) return false;
                                                    if (category.slug === 'pierscienie-centrujace' && homepageSettings.categoryHubRings === false) return false;
                                                    if (category.slug === 'zabezpieczenia' && homepageSettings.categoryLocks === false) return false;
                                                    if (category.slug === 'sruby' && homepageSettings.categoryBolts === false) return false;
                                                    if (category.slug === 'felgi-premium' && homepageSettings.categoryPremium === false) return false;
                                                    if (category.slug === 'felgi-stalowe' && homepageSettings.categorySteel === false) return false;
                                                    if (category.slug === 'opony-letnie' && homepageSettings.categorySummer === false) return false;
                                                    if (category.slug === 'opony-zimowe' && homepageSettings.categoryWinter === false) return false;
                                                    return true;
                                                }).map((category) => (
                                                    <a
                                                        key={category.id}
                                                        href={`/products/${category.slug}`}
                                                        className="group/item flex items-center gap-3 px-3 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white rounded-xl transition-all border border-transparent hover:border-white/5"
                                                    >
                                                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 group-hover/item:bg-cyan-500/20 group-hover/item:text-cyan-300 transition-colors border border-white/5 group-hover/item:border-cyan-500/20">
                                                            {category.name.includes('Felgi') ? <AlloyIcon /> : 
                                                             category.name.includes('Opony') ? <SparkIcon /> : 
                                                             <FlameIcon />}
                                                        </span>
                                                        <span className="font-semibold tracking-tight text-gray-200 group-hover/item:text-white text-xs">{category.name}</span>
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Dobór felg - wyciągnięte na wierzch, przed Felgi aluminiowe */}
                                <a
                                    key={navItems[1].name}
                                    href={navItems[1].href}
                                    className="group flex items-center gap-1.5 xl:gap-2 rounded-xl px-2 xl:px-3 py-2 font-semibold text-gray-200 transition-all hover:bg-white/5 hover:text-white whitespace-nowrap flex-shrink-0"
                                >
                                    <span className="flex h-8 w-8 xl:h-9 xl:w-9 items-center justify-center rounded-lg bg-cyan-500/10 text-cyan-300 group-hover:bg-cyan-500/20 group-hover:text-white transition-all">
                                        {navItems[1].icon}
                                    </span>
                                    <span className="flex flex-col text-left">
                                        <span className="uppercase tracking-[0.08em] text-[11px] xl:text-xs leading-tight font-bold">{navItems[1].name}</span>
                                    </span>
                                </a>

                                {/* Felgi aluminiowe - przed Narzędziami */}
                                <a
                                    key={navItems[2].name}
                                    href={navItems[2].href}
                                    className="group flex items-center gap-1.5 xl:gap-2 rounded-xl px-2 xl:px-3 py-2 font-semibold text-gray-200 transition-all hover:bg-white/5 hover:text-white whitespace-nowrap flex-shrink-0"
                                >
                                    <span className="flex h-8 w-8 xl:h-9 xl:w-9 items-center justify-center rounded-lg bg-cyan-500/10 text-cyan-300 group-hover:bg-cyan-500/20 group-hover:text-white transition-all">
                                        {navItems[2].icon}
                                    </span>
                                    <span className="flex flex-col text-left">
                                        <span className="uppercase tracking-[0.08em] text-[11px] xl:text-xs leading-tight font-bold">{navItems[2].name}</span>
                                    </span>
                                </a>

                                {/* Narzędzia dropdown */}
                                <div className="relative"
                                    onMouseEnter={() => { if (menuTimeout) clearTimeout(menuTimeout); setShowToolsMenu(true); }}
                                    onMouseLeave={() => { const t = setTimeout(() => setShowToolsMenu(false), 200); setMenuTimeout(t); }}
                                >
                                    <button
                                        onClick={() => setShowToolsMenu(!showToolsMenu)}
                                        className={`group flex items-center gap-1.5 xl:gap-2 rounded-xl px-2 xl:px-3 py-2 font-bold transition-all whitespace-nowrap flex-shrink-0 ${showToolsMenu ? 'bg-cyan-500/20 text-white ring-1 ring-cyan-500/40' : 'text-gray-200 hover:bg-white/5 hover:text-white'}`}
                                    >
                                        <span className="flex h-8 w-8 xl:h-9 xl:w-9 items-center justify-center rounded-lg bg-cyan-500/10 text-cyan-300 group-hover:bg-cyan-500/20 transition-all">
                                            <CalcIcon />
                                        </span>
                                        <span className="uppercase tracking-[0.08em] text-[11px] xl:text-xs leading-tight font-bold">Narzędzia</span>
                                        <svg className={`w-3 h-3 ml-0.5 transition-transform duration-300 ${showToolsMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                    </button>
                                    <div className={`absolute top-full left-0 mt-3 w-56 bg-[#0d121f] border border-cyan-500/30 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.8),0_0_30px_rgba(34,211,238,0.2)] z-[60] transition-all duration-300 origin-top-left ${showToolsMenu ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'}`}>
                                        <div className="p-2">
                                            <a href="/kalkulator-et" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-300 hover:bg-white/5 hover:text-white transition-all">
                                                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 flex-shrink-0"><CalcIcon /></span>
                                                <div><div className="text-xs font-bold text-white">Kalkulator ET</div><div className="text-[10px] text-gray-400">Offset i szerokość felgi</div></div>
                                            </a>
                                            <a href="/kalkulator-opon" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-300 hover:bg-white/5 hover:text-white transition-all">
                                                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 flex-shrink-0"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" strokeWidth={1.5}/><circle cx="12" cy="12" r="3.5" strokeWidth={1.5}/></svg></span>
                                                <div><div className="text-xs font-bold text-white">Kalkulator opon</div><div className="text-[10px] text-gray-400">Zamiennik rozmiaru</div></div>
                                            </a>
                                            <a href="/felgi-do-samochodu" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-300 hover:bg-white/5 hover:text-white transition-all">
                                                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 flex-shrink-0"><AlloyIcon /></span>
                                                <div><div className="text-xs font-bold text-white">Felgi do samochodu</div><div className="text-[10px] text-gray-400">Marki, modele i generacje</div></div>
                                            </a>
                                        </div>
                                    </div>
                                </div>

                                {/* Pozostałe navItems */}
                                {navItems.slice(3).map(item => (
                                    <a
                                        key={item.name}
                                        href={item.href}
                                        className="group flex items-center gap-1.5 xl:gap-2 rounded-xl px-2 xl:px-3 py-2 font-semibold text-gray-200 transition-all hover:bg-white/5 hover:text-white whitespace-nowrap flex-shrink-0"
                                    >
                                        <span className="flex h-8 w-8 xl:h-9 xl:w-9 items-center justify-center rounded-lg bg-cyan-500/10 text-cyan-300 group-hover:bg-cyan-500/20 group-hover:text-white transition-all">
                                            {item.icon}
                                        </span>
                                        <span className="flex flex-col text-left">
                                            <span className="uppercase tracking-[0.08em] text-[11px] xl:text-xs leading-tight font-bold">{item.name}</span>
                                        </span>
                                    </a>
                                ))}
                            </div>
                            <div className="fc-header-actions flex items-center gap-1 xl:gap-3 text-sm flex-shrink-0 ml-1 border-l border-white/10 pl-1 xl:pl-3">
                                {!isStandalone && (
                                    <button
                                        onClick={handleInstallClick}
                                        className="flex items-center gap-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 px-2.5 py-1.5 rounded-lg shadow-sm transition-all active:scale-95 mr-1"
                                        title="Zainstaluj aplikację FELGOCAR"
                                    >
                                        <DownloadIcon />
                                        <span className="text-xs font-semibold hidden xl:inline">Aplikacja</span>
                                    </button>
                                )}
                                <div className="scale-75 xl:scale-90 origin-right">
                                    <LanguageSwitcher />
                                </div>
                                <div className="flex items-center gap-3 xl:gap-4 px-1">
                                    <GarageIcon />
                                    <WishlistIcon count={wishlistCount} />
                                    <CartIcon count={cartCount} />
                                </div>
                                {currentUser ? (
                                    <div className="flex items-center gap-2 xl:gap-3 rounded-full border border-cyan-500/25 bg-[linear-gradient(135deg,rgba(15,23,42,0.96),rgba(9,15,29,0.92),rgba(15,118,110,0.18))] px-2.5 py-1.5 shadow-[0_12px_28px_rgba(34,211,238,0.14)] backdrop-blur-sm">
                                        <a href="/account" className="flex items-center gap-2 text-white hover:text-cyan-200 transition-colors text-sm whitespace-nowrap" aria-label="Moje Konto">
                                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-cyan-300 via-sky-300 to-emerald-300 text-slate-900 shadow-[0_6px_18px_rgba(103,232,249,0.35)]">
                                                <UserIcon />
                                            </span>
                                            <span className="hidden xl:inline text-[11px] font-semibold uppercase tracking-[0.12em]" aria-hidden="true">Moje Konto</span>
                                        </a>
                                        <button onClick={onLogout} className="hidden xl:inline rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-200 transition hover:border-red-400/40 hover:bg-red-500/10 hover:text-red-200">Wyloguj</button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <a href="/login" className="text-gray-300 hover:text-white transition-colors text-[11px] whitespace-nowrap hidden xl:inline">Zaloguj</a>
                                        <a href="/register" className="text-[#0b111d] bg-gradient-to-r from-cyan-400 to-emerald-400 hover:from-cyan-300 hover:to-emerald-300 px-3 py-1.5 rounded-full font-semibold transition-colors shadow-lg shadow-cyan-400/20 whitespace-nowrap text-[10px] xl:text-[11px]">
                                            Rejestracja
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mobile/Tablet Navigation - Visible only on smaller screens */}
                <div className="fc-mobile-header flex items-center justify-between lg:hidden py-2 sm:py-3">
                    {/* Logo or Brand */}
                    <a href="/" className="fc-logo-link" aria-label="FelgoCar — strona główna">
                        <picture>
                            <source media="(max-width: 374px)" srcSet="/logo/felgocar-icon-20261020.png" />
                            <img src="/logo/felgocar-main-20261020.webp" alt="FelgoCar.pl" width="660" height="220" className="fc-site-logo" fetchPriority="high" decoding="async" />
                        </picture>
                    </a>
                    
                    <div className="flex items-center gap-2 xs:gap-3 sm:gap-4">
                        {!isStandalone && (
                             <button
                                onClick={handleInstallClick}
                                className="flex items-center justify-center p-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30 active:scale-95 transition-all animate-pulse"
                                aria-label="Zainstaluj aplikację"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 xs:h-6 xs:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                            </button>
                        )}
                        <div className="scale-90 xs:scale-100 sm:scale-110 origin-right">
                            <LanguageSwitcher />
                        </div>
                        <CartIcon count={cartCount} />
                        <WishlistIcon count={wishlistCount} />
                        <button
                            onClick={() => setIsMenuOpen(true)}
                            className="flex items-center justify-center w-10 h-10 xs:w-11 xs:h-11 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/30 hover:border-cyan-400 transition-all active:scale-95"
                            aria-label="Open menu"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16m-7 6h7" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
            </div>
            
            {/* Mobile Menu Overlay */}
            <div
                className={`fixed inset-0 bg-black/98 z-40 transition-opacity duration-300 ease-in-out lg:hidden ${isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={() => setIsMenuOpen(false)}
                aria-hidden="true"
            ></div>

            {/* Mobile Menu Panel */}
            <div
                className={`fixed top-0 right-0 h-full w-[85%] xs:w-[80%] xs:max-w-sm sm:max-w-md bg-[#0b111d] border-l border-cyan-500/40 z-50 transform transition-transform duration-300 ease-in-out shadow-2xl shadow-black lg:hidden ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
                aria-label="Menu mobilne"
                role="dialog"
                aria-modal="true"
            >
                <div className="flex justify-between items-center px-4 py-3 border-b border-cyan-500/40 bg-[#0f1520]">
                    <span className="text-lg font-black text-white tracking-[0.2em] uppercase bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Menu</span>
                    <button
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center justify-center w-9 h-9 rounded-lg bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30 hover:border-red-400 transition-all active:scale-95"
                        aria-label="Close menu"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <nav className="flex flex-col p-3 space-y-2 overflow-y-auto max-h-[calc(100vh-60px)]">
                    {!isStandalone && (
                        <button
                            onClick={() => {
                                handleInstallClick();
                                if (deferredPrompt) setIsMenuOpen(false);
                            }}
                            className="flex items-center gap-3 w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-4 py-3 rounded-xl shadow-lg shadow-emerald-500/20 active:scale-[0.98] transition-all mb-2 border border-emerald-400/30"
                        >
                            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-400/20 text-emerald-100 border border-emerald-400/30">
                                <DownloadIcon />
                            </span>
                            <span className="flex flex-col text-left">
                                <span className="uppercase tracking-[0.1em] text-xs font-black">Zainstaluj Aplikację</span>
                                <span className="text-[10px] text-emerald-100/80">Pobierz FELGOCAR na swój telefon</span>
                            </span>
                        </button>
                    )}
                    {currentUser && (
                        <div className="mb-2 rounded-2xl border border-cyan-500/30 bg-[linear-gradient(135deg,rgba(15,23,42,0.96),rgba(9,15,29,0.92),rgba(15,118,110,0.10))] p-3 shadow-[0_14px_32px_rgba(15,23,42,0.28)]">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-cyan-300 via-sky-300 to-emerald-300 text-sm font-black text-slate-900 shadow-[0_8px_20px_rgba(103,232,249,0.35)]">{(currentUser.name || 'K').charAt(0).toUpperCase()}</div>
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-white">{currentUser.name}</p>
                                        <p className="text-[10px] uppercase tracking-[0.18em] text-cyan-300">Klient premium</p>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3 grid grid-cols-2 gap-2">
                                <a href="/account" onClick={() => setIsMenuOpen(false)} className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-center text-[10px] font-bold uppercase tracking-[0.12em] text-cyan-200 transition hover:bg-cyan-500/20">
                                    Konto
                                </a>
                                <button onClick={() => { onLogout(); setIsMenuOpen(false); }} className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-center text-[10px] font-bold uppercase tracking-[0.12em] text-red-200 transition hover:bg-red-500/20">
                                    Wyloguj
                                </button>
                            </div>
                        </div>
                    )}
                    {navItems.map(item => (
                        <a 
                            key={item.name} 
                            href={item.href} 
                            className="flex items-center gap-3 rounded-lg border border-cyan-500/20 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 px-3 py-3 text-sm font-bold text-white hover:border-cyan-400/50 hover:from-cyan-500/20 hover:to-blue-500/20 transition-all active:scale-[0.98]"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-500/30 to-blue-500/30 text-cyan-300 border border-cyan-500/40 flex-shrink-0">
                                {item.icon}
                            </span>
                            <span className="flex flex-col flex-1 min-w-0">
                                <span className="uppercase tracking-[0.1em] text-xs font-black truncate">{item.name}</span>
                                {item.accent && <span className="text-[10px] text-cyan-200/70 truncate">{item.accent}</span>}
                            </span>
                        </a>
                    ))}

                    {/* Kategorie produktów - Mobile */}
                    {categories.length > 0 && (
                        <div className="space-y-2 pt-2">
                             <div className="flex items-center gap-2 px-1">
                                <div className="h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent flex-1"></div>
                                <span className="text-[10px] font-black text-cyan-400 uppercase tracking-[0.15em] px-2 py-0.5 bg-cyan-500/20 rounded border border-cyan-500/30">Kategorie</span>
                                <div className="h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent flex-1"></div>
                            </div>
                            <div className="grid grid-cols-1 gap-1.5">
                                {categories.filter(category => {
                                    if (category.slug === 'felgi-aluminiowe' && homepageSettings.categoryAluminum === false) return false;
                                    if (category.slug === 'akcesoria' && homepageSettings.categoryAccessories === false) return false;
                                    if (category.slug === 'dystanse' && homepageSettings.categorySpacers === false) return false;
                                    if (category.slug === 'nakretki' && homepageSettings.categoryNuts === false) return false;
                                    if (category.slug === 'pierscienie-centrujace' && homepageSettings.categoryHubRings === false) return false;
                                    if (category.slug === 'zabezpieczenia' && homepageSettings.categoryLocks === false) return false;
                                    if (category.slug === 'sruby' && homepageSettings.categoryBolts === false) return false;
                                    if (category.slug === 'felgi-premium' && homepageSettings.categoryPremium === false) return false;
                                    if (category.slug === 'felgi-stalowe' && homepageSettings.categorySteel === false) return false;
                                    if (category.slug === 'opony-letnie' && homepageSettings.categorySummer === false) return false;
                                    if (category.slug === 'opony-zimowe' && homepageSettings.categoryWinter === false) return false;
                                    return true;
                                }).map(category => (
                                    <a
                                        key={category.id}
                                        href={`/products/${category.slug}`}
                                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-gradient-to-r from-white/5 to-white/10 border border-white/20 text-white hover:border-cyan-400/50 hover:from-cyan-500/10 hover:to-blue-500/10 active:scale-[0.98] transition-all text-xs font-semibold"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <span className="text-cyan-400 flex-shrink-0">
                                            {category.name.includes('Felgi') ? <AlloyIcon /> : 
                                             category.name.includes('Opony') ? <SparkIcon /> : 
                                             <FlameIcon />}
                                        </span>
                                        <span className="flex-1 truncate">{category.name}</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Narzędzia - Mobile */}
                    <div className="space-y-2 pt-2">
                        <div className="flex items-center gap-2 px-1">
                            <div className="h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent flex-1"></div>
                            <span className="text-[10px] font-black text-cyan-400 uppercase tracking-[0.15em] px-2 py-0.5 bg-cyan-500/20 rounded border border-cyan-500/30">Narzędzia</span>
                            <div className="h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent flex-1"></div>
                        </div>
                        <a
                            href="/kalkulator-et"
                            className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-gradient-to-r from-white/5 to-white/10 border border-white/20 text-white hover:border-cyan-400/50 hover:from-cyan-500/10 hover:to-blue-500/10 active:scale-[0.98] transition-all text-xs font-semibold"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            <span className="text-cyan-400 flex-shrink-0"><CalcIcon /></span>
                            <span className="flex-1">Kalkulator ET</span>
                        </a>
                        <a
                            href="/kalkulator-opon"
                            className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-gradient-to-r from-white/5 to-white/10 border border-white/20 text-white hover:border-cyan-400/50 hover:from-cyan-500/10 hover:to-blue-500/10 active:scale-[0.98] transition-all text-xs font-semibold"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            <span className="text-cyan-400 flex-shrink-0"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" strokeWidth={1.5}/><circle cx="12" cy="12" r="3.5" strokeWidth={1.5}/><line x1="12" y1="3" x2="12" y2="8.5" strokeWidth={1.5} strokeLinecap="round"/><line x1="12" y1="15.5" x2="12" y2="21" strokeWidth={1.5} strokeLinecap="round"/><line x1="3" y1="12" x2="8.5" y2="12" strokeWidth={1.5} strokeLinecap="round"/><line x1="15.5" y1="12" x2="21" y2="12" strokeWidth={1.5} strokeLinecap="round"/></svg></span>
                            <span className="flex-1">Kalkulator opon</span>
                        </a>
                        <a
                            href="/felgi-do-samochodu"
                            className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-gradient-to-r from-white/5 to-white/10 border border-white/20 text-white hover:border-cyan-400/50 hover:from-cyan-500/10 hover:to-blue-500/10 active:scale-[0.98] transition-all text-xs font-semibold"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            <span className="text-cyan-400 flex-shrink-0"><AlloyIcon /></span>
                            <span className="flex-1">Felgi do samochodu</span>
                        </a>
                    </div>

                     <div className="border-t border-cyan-500/30 pt-2 space-y-2 mt-2">
                        <a href="/account?section=garage" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-gradient-to-r from-white/5 to-white/10 border border-white/20 text-sm font-semibold text-white hover:border-cyan-400/50 hover:from-cyan-500/10 hover:to-blue-500/10 transition-all active:scale-[0.98]">
                            <span className="text-cyan-400 flex-shrink-0"><GarageGlyph /></span>
                            <span>Mój garaż</span>
                        </a>
                        {currentUser ? (
                             <>
                                <a href="/account" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-gradient-to-r from-white/5 to-white/10 border border-white/20 text-sm font-semibold text-white hover:border-cyan-400/50 hover:from-cyan-500/10 hover:to-blue-500/10 transition-all active:scale-[0.98]">
                                    <UserIcon />
                                    <span>Moje Konto</span>
                                </a>
                                <button onClick={() => { onLogout(); setIsMenuOpen(false); }} className="flex items-center justify-center gap-2.5 w-full px-3 py-2.5 rounded-lg bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30 text-sm font-semibold text-red-300 hover:border-red-400/50 hover:from-red-500/30 hover:to-orange-500/30 transition-all active:scale-[0.98]">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                    </svg>
                                    <span>Wyloguj</span>
                                </button>
                            </>
                        ) : (
                            <>
                                <a href="/login" onClick={() => setIsMenuOpen(false)} className="flex items-center justify-center gap-2.5 px-3 py-3 rounded-lg bg-gradient-to-r from-white/5 to-white/10 border border-white/20 text-sm font-semibold text-white hover:border-cyan-400/50 hover:from-cyan-500/10 hover:to-blue-500/10 transition-all active:scale-[0.98]">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                                    </svg>
                                    <span>Zaloguj się</span>
                                </a>
                                <a href="/register" onClick={() => setIsMenuOpen(false)} className="flex items-center justify-center gap-2.5 w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-sm font-bold py-3.5 rounded-lg transition-all shadow-lg shadow-cyan-500/30 hover:shadow-cyan-400/40 active:scale-[0.98] border border-cyan-400/50">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                    </svg>
                                    <span>Zarejestruj się</span>
                                </a>
                            </>
                        )}
                    </div>
                </nav>
            </div>
        </header>
    );
};

export default Header;
